import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment/environment';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TermsPrivacyModalComponent } from '../terms-privacy-modal';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { StripePaymentFormConfig } from '../stripe-payment-method/stripe-payment-form.component';
import { SafeHtml } from '@angular/platform-browser';
import { AssurantLegalContentType } from '@app/assurant/assurant.service';

// Stripe Type Definitions
interface StripeElement {
  mount(element: HTMLElement): void;
  addEventListener(event: string, handler: (event: StripeElementChangeEvent) => void): void;
}

interface StripeElements {
  create(type: string, options?: Record<string, unknown>): StripeElement;
}

interface StripeInstance {
  elements(): StripeElements;
  createPaymentMethod(options: Record<string, unknown>): Promise<{ paymentMethod?: { id: string; [key: string]: unknown }; error?: { message: string } }>;
  paymentRequest(options: Record<string, unknown>): PaymentRequest;
}

interface StripeElementChangeEvent {
  complete: boolean;
  brand?: string;
  error?: { message: string };
}

interface PaymentRequest {
  canMakePayment(): Promise<{ applePay?: boolean; googlePay?: boolean }>;
  on(event: string, handler: (event: PaymentRequestPaymentEvent) => void): void;
}

interface PaymentRequestPaymentEvent {
  paymentMethod: { id: string; [key: string]: unknown };
  complete(status: string): void;
}

declare let Stripe: (key: string, options?: Record<string, unknown>) => StripeInstance;

@Component({
    selector: "app-st-elements-qbe",
    templateUrl: "./st-elements-qbe.component.html",
    styleUrls: ["./st-elements-qbe.component.scss"],
    standalone: false
})
export class StElementsQbeComponent implements OnInit, OnDestroy {
  @Input() loading: boolean;
  @Input() hasBackButton = false;
  @Input() totalPayable: number;
  @Input() kioskRules = false;
  @Input() isAssurant = false;
  @Input() privacyPolicyContent: SafeHtml;
  @Output() cardComplete = new EventEmitter<{ brand: string }>();
  @Output() cardFilled = new EventEmitter<{ id: string; [key: string]: unknown }>();
  @Output() declinePayment = new EventEmitter<void>();
  @Output() assurantDisclosureDocumentSelected: EventEmitter<AssurantLegalContentType> = new EventEmitter<AssurantLegalContentType>();
  @ViewChild("cardElement") cardElement: ElementRef;
  @ViewChild("cardNumberElement") cardNumberElement: ElementRef;
  @ViewChild("cardExpElement") cardExpElement: ElementRef;
  @ViewChild("cardCvcElement") cardCvcElement: ElementRef;
  @ViewChild("cardPostalCode") cardPostalCode: ElementRef;
  @ViewChild("paymentRequestButton") paymentRequestButton: ElementRef;

  formConfig: StripePaymentFormConfig = {
    formTitle: "Add Payment Method",
    formSubtitle: "Add a credit or debit card",
    showName: false,
    showZipCode: true,
    showApplePay: true,
    submitButtonText: "Purchase",
    cancelButtonText: "Go Back",
    stripePublicKey: environment.stripePublishableKey,
    locale: "en",
    save_button: this.translate.instant("total_premium.purchase"),
  };

  disabledBtn = false;
  stripe: StripeInstance;
  cardNumber: StripeElement;
  cardErrors: string | null = null;
  subscription: Subscription = new Subscription();

  paymentRequest: PaymentRequest;
  prButton: StripeElement;
  confirmation: unknown;

  checkboxesForm: FormGroup;
  checkboxError$: Observable<string | null>;

  private readonly showValidationErrorSubject = new BehaviorSubject<boolean>(false);

  protected readonly AssurantLegalContentType = AssurantLegalContentType;

  constructor(
    private translate: TranslateService,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.checkboxesForm = this.fb.group({
      termsAgreement: [false, Validators.requiredTrue],
      kioskRules: [false],
      receiveDocumentsByEmail: [true],
      disclosureAgreement: [false]
    });

    if (this.kioskRules) {
      this.checkboxesForm.get('kioskRules')?.setValidators([Validators.requiredTrue]);
      this.checkboxesForm.get('kioskRules')?.updateValueAndValidity();
    }

    if(this.isAssurant) {
      this.checkboxesForm.get('disclosureAgreement')?.setValidators([Validators.requiredTrue]);
      this.checkboxesForm.get('disclosureAgreement')?.updateValueAndValidity();

      // receiveDocumentsByEmail is optional - no validator needed
    }

    this.checkboxError$ = combineLatest([
      this.showValidationErrorSubject,
      this.checkboxesForm.valueChanges.pipe(startWith(this.checkboxesForm.value))
    ]).pipe(
      map(([showError]) => {
        if (showError && this.checkboxesForm.invalid) {
          return this.translate.instant("form.checkout_agree_to_terms_error");
        }

        return null;
      }),
      distinctUntilChanged()
    );
  }

  async mountStripe(language: string): Promise<void> {
    const style = {
      base: {
        color: "#14171C",
        lineHeight: "24px",
        fontSize: "16px",
        fontWeight: "500",
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#667080",
        },
      },
    };

    this.stripe = Stripe(environment.stripePublishableKey, { locale: language });
    const elements = this.stripe.elements();

    this.cardNumber = elements.create("cardNumber", {
      style,
      placeholder: "**** **** **** ****",
    });
    this.cardNumber.mount(this.cardNumberElement.nativeElement);

    const cardExpiryElement = elements.create("cardExpiry", {
      style,
      placeholder: "MM / YY",
    });
    cardExpiryElement.mount(this.cardExpElement.nativeElement);

    const cardCvcElement = elements.create("cardCvc", {
      style,
      placeholder: "CVC",
    });
    cardCvcElement.mount(this.cardCvcElement.nativeElement);
    this.cardNumber.addEventListener("change", (val: StripeElementChangeEvent) => {
      if (val.complete === true) {
        this.cardComplete.emit({ brand: val.brand || '' });
      }
      const error = val.error;
      this.cardErrors = error && error.message;
    });

    cardExpiryElement.addEventListener("change", ({ error }: StripeElementChangeEvent) => {
      this.cardErrors = error && error.message;
    });

    cardCvcElement.addEventListener("change", ({ error }: StripeElementChangeEvent) => {
      this.cardErrors = error && error.message;
    });

    this.paymentRequest = this.stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Pay today",
        amount: this.totalPayable,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: ["link", "browserCard", "googlePay"],
    });

    const result = await this.paymentRequest.canMakePayment();
    if (result && result.applePay === true) {
      this.prButton = elements.create("paymentRequestButton", {
        paymentRequest: this.paymentRequest,
        style: {
          paymentRequestButton: {
            type: "default",
            theme: "dark",
            height: "48px",
          },
        },
      });

      this.prButton.mount(this.paymentRequestButton.nativeElement);

      this.paymentRequest.on("paymentmethod", async (ev: PaymentRequestPaymentEvent) => {
        if (!this.termsAgreementControl.value) {
          ev.complete("fail");
          return;
        }

        this.cardErrors = null;
        try {
          ev.complete("success");
          this.cardFilled.emit(ev.paymentMethod);
        } catch (error: unknown) {
          ev.complete("fail");
          this.cardErrors = (error as Error).message;
          this.disabledBtn = false;
        }
      });
    }
  }

  onApplePayButtonClick() {
    this.showValidationErrorSubject.next(true);
  }

  private validateCheckboxes(): boolean {
    this.showValidationErrorSubject.next(true);
    return this.checkboxesForm.valid;
  }

  decline() {
    this.declinePayment.emit();
  }

  onFormSubmit(data: { id: string; [key: string]: unknown }): void {
    if (!this.validateCheckboxes()) {
      this.disabledBtn = false;
      return;
    }

    console.log('Received response from Payment Provider', data);

    if (data?.['paymentMethodId'] || data.id) {
      this.cardFilled.emit(data);
    } else {
      this.cardErrors = "There was a problem handling your payment. We received a result from the payment provider but wasn't able to process it.";
      this.disabledBtn = false;
    }
  }

  async handleForm(e: Event): Promise<void> {
    this.disabledBtn = true;
    this.cardErrors = null;
    e.preventDefault();

    if (!this.validateCheckboxes()) {
      this.disabledBtn = false;
      return;
    }

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      type: "card",
      card: this.cardNumber,
      billing_details: {
        address: {},
      },
    });

    if (error) {
      this.cardErrors = error.message;
      this.disabledBtn = false;
    } else {
      this.cardFilled.emit(paymentMethod);
    }
  }

  onAgreementTextClick(event: MouseEvent): void {
    if (event.target instanceof HTMLElement && event.target.nodeName === "A") {
      event.preventDefault();
      event.stopPropagation();
      this.dialog.open(TermsPrivacyModalComponent, {
        maxWidth: "80vw",
        maxHeight: "80vh",
        data: {
          privacyPolicyContent: this.privacyPolicyContent
        }
      });
    }
  }

  onAssurantDisclosureTextClick(event: MouseEvent, type: AssurantLegalContentType): void {
    if (event.target instanceof HTMLElement && event.target.nodeName === "A") {
      event.preventDefault();
      event.stopPropagation();

      this.assurantDisclosureDocumentSelected.emit(type);
    }
  }

  onFormError(error: string) {
    console.error("Form Error:", error);
    this.showValidationErrorSubject.next(true);
  }

  get termsAgreementControl(): FormControl {
    return this.checkboxesForm.get('termsAgreement') as FormControl;
  }

  get kioskRulesControl(): FormControl {
    return this.checkboxesForm.get('kioskRules') as FormControl;
  }

  get disclosureAgreementControl(): FormControl {
    return this.checkboxesForm.get('disclosureAgreement') as FormControl;
  }

  get receiveDocumentsByEmailControl(): FormControl {
    return this.checkboxesForm.get('receiveDocumentsByEmail') as FormControl;
  }

  get termsAccepted(): boolean {
    return this.termsAgreementControl.value === true;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.showValidationErrorSubject.complete();
  }
}
